import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload } from 'lucide-react';

interface FileDropZoneProps {
  onFilesDropped: (files: FileList) => void | Promise<void>;
  disabled?: boolean;
  message?: string;
  uploadingMessage?: string;
  isUploading?: boolean;
  children: React.ReactNode;
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFilesDropped,
  disabled = false,
  message = 'Drop files here to attach',
  uploadingMessage = 'Uploading...',
  isUploading = false,
  children,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  // Counter handles dragenter/dragleave firing for every child element,
  // which would otherwise flicker the overlay off while moving the cursor.
  const dragCounter = useRef(0);

  const reset = useCallback(() => {
    dragCounter.current = 0;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (disabled) {
      reset();
      return;
    }

    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes('Files');

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounter.current += 1;
      setIsDragging(true);
    };

    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDragging(false);
    };

    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      reset();
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        void onFilesDropped(files);
      }
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [disabled, onFilesDropped, reset]);

  const showOverlay = (isDragging || isUploading) && !disabled;

  return (
    <>
      {children}
      {showOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" />
          <div className="relative flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-brand bg-background px-12 py-10 shadow-2xl">
            <Upload className="h-10 w-10 text-brand" />
            <p className="text-base font-medium text-brand text-center">
              {isUploading ? uploadingMessage : message}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default FileDropZone;
